import axios from 'axios';
import { toast } from 'react-toastify';

import LocaleService from '@services/LocaleService';

const i18n = LocaleService.getTranslations('general.appUpdate');

/** How often an open tab checks for a newer deploy. */
const CHECK_EVERY_MS = 5 * 60 * 1000;

type RouterState = { location: { key: string } };

type Router = {
  state: RouterState;
  subscribe: (listener: (state: RouterState) => void) => () => void;
};

/**
 * CacheBuster only checks for a new build when the app loads, so a tab left
 * open across a deploy keeps running the old code. This keeps checking
 * meta.json while the app is open (every few minutes, and whenever the tab is
 * shown again). Once a newer build is live, the next page change becomes a full
 * load, so the user lands on the new version without losing anything they are
 * typing. A toast also offers an immediate refresh.
 */
const startVersionWatcher = (router: Router) => {
  let loadedVersion: string | null = null;
  let updateAvailable = false;

  const fetchVersion = async (): Promise<string | null> => {
    try {
      const { data } = await axios.get(`/meta.json?t=${Date.now()}`);
      return data?.timestamp ? String(data.timestamp) : null;
    } catch {
      return null;
    }
  };

  const check = async () => {
    if (updateAvailable) return;
    const version = await fetchVersion();
    if (!version) return;
    if (loadedVersion === null) {
      loadedVersion = version;
      return;
    }
    if (version !== loadedVersion) {
      updateAvailable = true;
      toast(i18n.available, {
        toastId: 'app-update',
        autoClose: false,
        onClick: () => window.location.reload(),
      });
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') check();
  };

  // Start from the page the tab is on: the router does not emit on load, so
  // the first page change after a deploy is the one that must reload.
  let lastLocationKey = router.state.location.key;
  const unsubscribe = router.subscribe((state) => {
    const { key } = state.location;
    if (updateAvailable && key !== lastLocationKey) {
      window.location.reload();
    }
    lastLocationKey = key;
  });

  check();
  const timer = window.setInterval(check, CHECK_EVERY_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    unsubscribe();
  };
};

export default startVersionWatcher;
